import { registerCommand } from '../commands';
import { renderPdfBytes } from '../display/pdf';
import type { ViewerShell } from '../shell';
import { messageDialog, openDialog, paragraph, snapshotViewport } from '../ui/dialogs';
import { RisError } from './client';
import { risClient } from './session';
import { activeStudyUID, refreshStudySeries, saveBlob } from './util';

/**
 * Study actions that go through ris (viewer contract §6): "Captura Dicom" (the active viewport with its
 * measurements, stored as a Secondary Capture), "Adjuntar PDF" (Encapsulated PDF series), "Descargar
 * Dicom" (the study as a ZIP) and "Reporte" (the study report PDF in a half-screen panel).
 */

export const PACS_TEXT = {
  captureTitle: 'Captura Secundaria',
  captureOk: 'Su captura se ha cargado correctamente.',
  captureError: 'Se ha producido un error durante la carga. Por favor, inténtelo de nuevo.',
  attachTitle: 'Adjuntar PDF',
  attachIntro: 'Seleccione un archivo PDF (máximo 20 MB) para adjuntarlo al estudio.',
  attachTitleLabel: 'Título del documento',
  attachFileLabel: 'Archivo PDF',
  attachOk: 'El documento PDF se adjuntó al estudio.',
  attachError: 'No fue posible adjuntar el documento. Por favor, inténtelo de nuevo.',
  attachNoFile: 'Seleccione un archivo PDF.',
  attachNotPdf: 'El archivo debe ser un PDF.',
  attachTooLarge: 'El archivo supera el tamaño máximo de 20 MB.',
  preparing: 'Preparando descarga…',
  downloadError: 'No fue posible descargar el estudio.',
  reportTitle: 'Reporte',
  reportLoading: 'Cargando ...',
  reportNotFound: 'Reporte no encontrado',
  reportError: 'No fue posible cargar el reporte.',
} as const;

const PDF_TYPE = 'application/pdf';
const MAX_PDF_BYTES = 20 * 1024 * 1024;
const MAX_TITLE = 64;
const PDF_MAGIC = '%PDF-';
const NOT_FOUND = 404;

// --- Captura Dicom ---------------------------------------------------------------------------------

async function captureActive(shell: ViewerShell): Promise<void> {
  const client = risClient();
  const viewport = shell.activeViewport();
  const series = viewport?.currentStack?.series;
  if (!client || !viewport || !series || !viewport.currentImage()) return;
  const canvas = snapshotViewport(viewport);
  const png = canvas ? await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png')) : null;
  let ok = false;
  if (png) {
    try {
      const result = await client.captureViewport(series.studyInstanceUID, png);
      ok = result.stored > 0;
    } catch (error) {
      console.warn('[viewer] capture failed', error);
    }
  }
  messageDialog(shell, PACS_TEXT.captureTitle, ok ? PACS_TEXT.captureOk : PACS_TEXT.captureError, 'capture-dialog');
  if (ok) await refreshSeries(shell, series.studyInstanceUID);
}

async function refreshSeries(shell: ViewerShell, studyUID: string): Promise<void> {
  try {
    await refreshStudySeries(shell, studyUID);
  } catch (error) {
    console.warn('[viewer] series refresh failed', error);
  }
}

// --- Adjuntar PDF ----------------------------------------------------------------------------------

async function isPdf(file: File): Promise<boolean> {
  const head = new Uint8Array(await file.slice(0, PDF_MAGIC.length).arrayBuffer());
  return String.fromCharCode(...head) === PDF_MAGIC;
}

function field(parent: HTMLElement, label: string, input: HTMLInputElement): void {
  const wrap = document.createElement('label');
  wrap.className = 'attach-field';
  const caption = document.createElement('span');
  caption.textContent = label;
  wrap.append(caption, input);
  parent.appendChild(wrap);
}

function openAttachDialog(shell: ViewerShell): void {
  const client = risClient();
  if (!client) return;
  const studyUID = activeStudyUID(shell);
  const file = document.createElement('input');
  file.type = 'file';
  file.accept = PDF_TYPE;
  file.className = 'attach-file';
  const title = document.createElement('input');
  title.type = 'text';
  title.className = 'attach-title';
  title.maxLength = MAX_TITLE;
  let status: HTMLParagraphElement | null = null;
  let sending = false;
  const { body } = openDialog(shell, {
    title: PACS_TEXT.attachTitle,
    className: 'attach-dialog',
    buttons: [
      { label: 'Cancelar', onClick: (close) => close() },
      {
        label: 'Adjuntar',
        primary: true,
        onClick: (close) => {
          if (sending) return;
          const say = (text: string) => {
            status ??= paragraph(body, '', 'attach-status');
            status.textContent = text;
          };
          const chosen = file.files?.[0];
          if (!chosen) return say(PACS_TEXT.attachNoFile);
          if (chosen.size > MAX_PDF_BYTES) return say(PACS_TEXT.attachTooLarge);
          sending = true;
          void (async () => {
            try {
              if (chosen.type && chosen.type !== PDF_TYPE) throw new TypeError(PACS_TEXT.attachNotPdf);
              if (!(await isPdf(chosen))) throw new TypeError(PACS_TEXT.attachNotPdf);
              const pdf = new Blob([await chosen.arrayBuffer()], { type: PDF_TYPE });
              await client.attachPdf(studyUID, pdf, title.value.trim() || chosen.name.replace(/\.pdf$/i, '').slice(0, MAX_TITLE));
              close();
              shell.toast(PACS_TEXT.attachOk);
              await refreshSeries(shell, studyUID);
            } catch (error) {
              console.warn('[viewer] attach failed', error);
              say(error instanceof TypeError && error.message === PACS_TEXT.attachNotPdf ? PACS_TEXT.attachNotPdf : PACS_TEXT.attachError);
            } finally {
              sending = false;
            }
          })();
        },
      },
    ],
  });
  paragraph(body, PACS_TEXT.attachIntro);
  field(body, PACS_TEXT.attachFileLabel, file);
  field(body, PACS_TEXT.attachTitleLabel, title);
}

// --- Descargar Dicom -------------------------------------------------------------------------------

let downloading = false;

async function downloadStudy(shell: ViewerShell): Promise<void> {
  const client = risClient();
  if (!client || downloading) return;
  const studyUID = activeStudyUID(shell);
  downloading = true;
  const progress = document.createElement('div');
  progress.className = 'download-progress';
  progress.setAttribute('role', 'status');
  progress.textContent = PACS_TEXT.preparing;
  shell.regions.overlayLayer.appendChild(progress);
  try {
    saveBlob(await client.studyArchive(studyUID), `${studyUID}.zip`);
  } catch (error) {
    console.warn('[viewer] study download failed', error);
    shell.toast(PACS_TEXT.downloadError, 'error');
  } finally {
    progress.remove();
    downloading = false;
  }
}

// --- Reporte ------------------------------------------------------------------------------------

let closeReport: (() => void) | null = null;

/** The half-screen report panel ("study_report" popup) showing the PDF with the PDF display. */
async function openReport(shell: ViewerShell): Promise<void> {
  const client = risClient();
  if (!client) return;
  closeReport?.();
  const studyUID = activeStudyUID(shell);
  const panel = document.createElement('section');
  panel.className = 'report-panel';
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-label', PACS_TEXT.reportTitle);
  const head = document.createElement('div');
  head.className = 'report-panel-head';
  const title = document.createElement('h2');
  title.textContent = PACS_TEXT.reportTitle;
  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.className = 'cmd-dialog-button report-close';
  closeButton.textContent = 'Cerrar';
  head.append(title, closeButton);
  const host = document.createElement('div');
  host.className = 'report-panel-body';
  const status = paragraph(host, PACS_TEXT.reportLoading, 'report-status');
  panel.append(head, host);
  shell.regions.overlayLayer.appendChild(panel);
  let dispose: (() => void) | null = null;
  const close = () => {
    dispose?.();
    panel.remove();
    if (closeReport === close) closeReport = null;
  };
  closeReport = close;
  closeButton.addEventListener('click', close);
  try {
    const blob = await client.studyReport(studyUID);
    if (!panel.isConnected) return;
    status.remove();
    dispose = renderPdfBytes(host, new Uint8Array(await blob.arrayBuffer()), `reporte-${studyUID}.pdf`);
  } catch (error) {
    console.warn('[viewer] report failed', error);
    status.textContent = error instanceof RisError && error.status === NOT_FOUND ? PACS_TEXT.reportNotFound : PACS_TEXT.reportError;
  }
}

export function install(shell: ViewerShell): void {
  if (!risClient()) return;
  registerCommand('secondary-capture', () => void captureActive(shell));
  registerCommand('attach-pdf', () => openAttachDialog(shell));
  registerCommand('download-dicom', () => void downloadStudy(shell));
  registerCommand('study-report', () => void openReport(shell));
}
